import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/api.service';
import { OfflineFileStore } from './offline-file-store';

/** Un archivo elegido sin conexión y copiado a la carpeta privada, esperando salir con la tarea. */
export interface StoredUpload {
  /** Ruta relativa a `Directory.Data`. */
  path: string;
  name: string;
  mime: string;
}

/**
 * Una entrega de tarea diferida: lo que `ApiService.taskUpdate` recibiría de haber habido conexión. Los
 * archivos viajan como referencias a disco (no como bytes dentro de SQLite) y se leen al enviar.
 */
export interface TaskUploadRequest {
  projectId: number;
  /** Los `files[]` del envío. */
  files: StoredUpload[];
  /** Todo lo demás del `FormData`: los `multimedias[i][key]` de lo ya guardado. */
  fields: [string, string][];
}

/** Método con el que la cola guarda una entrega de tarea (el cuerpo es un {@link TaskUploadRequest}). */
export const MULTIPART_METHOD = 'MULTIPART';

/**
 * Envía una entrega de tarea que quedó en la cola: rearma el `FormData` con los archivos leídos de
 * disco y lo manda con el mismo `taskUpdate` de siempre. Si el servidor lo acepta, borra los archivos.
 *
 * Una respuesta que no es `success` es un rechazo del servidor (la tarea ya no admite entregas, por
 * ejemplo): sube como un error 422 para que la cola lo marque como rechazo permanente y no lo reintente
 * sola. Un `fetch` que falla por la red sube tal cual y se reintenta.
 */
@Injectable({ providedIn: 'root' })
export class OfflineTaskUploadSender {
  constructor(
    private api: ApiService,
    private files: OfflineFileStore
  ) {}

  async send(request: TaskUploadRequest): Promise<void> {
    const formData = new FormData();
    for (const upload of request.files) {
      formData.append('files[]', await this.files.read(upload.path, upload.mime), upload.name);
    }
    for (const [key, value] of request.fields) {
      formData.append(key, value);
    }

    const response = await this.api.taskUpdate(request.projectId, formData);
    if (response?.type !== 'success') {
      throw Object.assign(new Error(response?.data?.msg ?? 'El servidor rechazó la entrega de la tarea'), {
        status: 422,
      });
    }
    await this.discard(request);
  }

  /** Borra los archivos de una entrega que ya no va a salir (enviada, o descartada junto con el curso). */
  async discard(request: TaskUploadRequest): Promise<void> {
    for (const upload of request.files) {
      await this.files.remove(upload.path);
    }
  }
}
